import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
} from "@material-ui/core";
import clsx from "clsx";
import { format, isSameDay, parseISO, subDays } from "date-fns";
import { formatMessageTime } from "../../utils/dateFormatters";
import { scrollToBottom, handleScrollLoadMore } from "../../utils/scrollHelpers";
import { isNil } from "lodash";

// 🎨 Estilos Modularizados
import { useMessageListAllStyles } from "./styles";

// 🎯 Constantes e Utilitários
import {
  MESSAGE_MEDIA_TYPES,
  TIMING_CONFIG,
  LAYOUT_CONFIG,
  ACK_STATUS,
  MESSAGE_ACTIONS,
  SYSTEM_MESSAGES,
  PAGINATION_CONFIG,
} from "./constants/messageConstants";

import {
  formatXml,
  isYouTubeLink,
  isXmlContent,
  truncateQuotedText,
} from "./utils/messageFormatters";
import React, {
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useTheme } from "@material-ui/core/styles";

import {
  AccessTime,
  Done,
  DoneAll,
  ExpandMore,
  MoreVert,
  Facebook,
  Instagram,
} from "@material-ui/icons";
import CallLogMessage from "../CallLogMessage";
import FloatingMessageActions from "../FloatingMessageActions";
import MarkdownWrapper from "../MarkdownWrapper";
import YouTubePreview from "../ModalYoutubeCors";
import TicketSeparator from "../TicketSeparator";
import SkeletonLoader from "../SkeletonLoader";
import ReactionDetailsModal from "../ReactionDetailsModal";
import LocationPreview from "../LocationPreview";
import MessageOptionsMenu from "../MessageOptionsMenu";
import ModalImageCors from "../ModalImageCors";
import AudioModal from "../AudioModal";

import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";

// 🎯 Componentes Modularizados (Fases 2 e 3)
import QuotedMessage from "./components/QuotedMessage";
import MessageAck from "./components/MessageAck";
import MessageBubbleLeft from "./components/MessageBubbleLeft";
import MessageBubbleRight from "./components/MessageBubbleRight";
import { useDragAndDrop, DragDropOverlay } from "./hooks/useDragAndDrop";

// 🎬 Renderização de Mídia (Fase 6)
import { useMediaRenderer } from "./hooks/useMediaRenderer";

// 🎵 Direct Audio Rendering - Bypass abstraction for audio
import WhatsAppAudioPlayer from "./components/WhatsAppAudioPlayer";
import audioSequenceManager from "../../utils/audioSequenceManager";
import { useAudioPlayer } from "../../context/AudioPlayerContext";

// 📇 Direct Contact Rendering - Bypass abstraction for contacts  
import VcardPreview from "../VcardPreview";
import { parseContactMessage } from "./utils/messageFormatters";

// 🎯 Hooks Modularizados (Fase 4)
import useMessageReactions from "./hooks/useMessageReactions";
import useMessageActions from "./hooks/useMessageActions";

import toastError from "../../errors/toastError";
import api from "../../services/api";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { useHistory, useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";

// 🔒 ErrorBoundary por item de mensagem para evitar tela branca e registrar detalhes
class MessageItemErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    try {
      const msg = this.props.message || {};
      // Log rico no console para diagnóstico rápido
      // Evita enviar para Sentry aqui para não duplicar com backend
      // eslint-disable-next-line no-console
      console.error("[MessagesList] Erro ao renderizar mensagem", {
        error,
        info,
        message: {
          id: msg.id,
          fromMe: msg.fromMe,
          mediaType: msg.mediaType,
          contactId: msg.contactId,
          hasContact: !!msg.contact,
          contactName: msg?.contact?.name,
          quotedHasContact: !!(msg.quotedMsg && msg.quotedMsg.contact),
          quotedContactName: msg?.quotedMsg?.contact?.name,
        }
      });
    } catch {}
  }

  render() {
    if (this.state.hasError) {
      const { message } = this.props;
      return (
        <div style={{
          padding: 8,
          margin: '4px 8px',
          borderRadius: 8,
          background: 'rgba(244, 67, 54, 0.08)',
          color: '#b71c1c',
          fontSize: 12
        }}>
          Problema ao exibir esta mensagem{message?.id ? ` (#${message.id})` : ''}. Ela foi ocultada para evitar travamento.
        </div>
      );
    }
    return this.props.children;
  }
}

const reducer = (state, action) => {
  if (action.type === MESSAGE_ACTIONS.LOAD_MESSAGES) {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      // 1) tenta casar por clientMessageId (evita duplicar temporárias)
      let messageIndex = -1;
      if (message.clientMessageId) {
        messageIndex = state.findIndex(
          (m) => m.clientMessageId && m.clientMessageId === message.clientMessageId
        );
      }
      // 2) se não achou, casa por id
      if (messageIndex === -1) {
        messageIndex = state.findIndex((m) => m.id === message.id);
      }

      if (messageIndex !== -1) {
        state[messageIndex] = { ...state[messageIndex], ...message };
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === MESSAGE_ACTIONS.ADD_MESSAGE) {
    const dbgOn = (flag) => (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(flag) === '1');
    const dbg = (...args) => { try { if (dbgOn('debugDedupe')) console.log(...args); } catch {} };
    const nm = action.payload;
    const newMessage = nm && typeof nm === 'object'
      ? { ...nm, isPrivate: nm?.isPrivate === true || nm?.isPrivate === 'true' }
      : nm;
    // 1) tenta casar por clientMessageId (fluxo de saída)
    let messageIndex = -1;
    if (newMessage.clientMessageId) {
      messageIndex = state.findIndex(
        (m) => m.clientMessageId && m.clientMessageId === newMessage.clientMessageId
      );
    }
    // 2) senão, tenta por id
    if (messageIndex === -1) {
      messageIndex = state.findIndex((m) => m.id === newMessage.id);
    }

    if (messageIndex !== -1) {
      dbg('[dedupe] merge by clientMessageId/id', { id: newMessage.id, clientMessageId: newMessage.clientMessageId });
      state[messageIndex] = { ...state[messageIndex], ...newMessage };
    } else {
      // Deduplicação defensiva: evita inserir bolha duplicada quando fromMe com mesma mídia
      const isFromMe = !!newMessage?.fromMe;
      const hasMedia = !!newMessage?.mediaUrl;
      if (isFromMe && hasMedia) {
        const newTs = new Date(newMessage.createdAt || newMessage.timestamp || Date.now()).getTime();
        const nearIdx = state.findIndex((m) => {
          if (!m?.fromMe || !m?.mediaUrl) return false;
          if (m.mediaUrl !== newMessage.mediaUrl) return false;
          const mTs = new Date(m.createdAt || m.timestamp || 0).getTime();
          return Math.abs(newTs - mTs) <= 5000; // 5s de tolerância
        });
        if (nearIdx !== -1) {
          dbg('[dedupe] merge by mediaUrl/time', { id: newMessage.id, mediaUrl: newMessage.mediaUrl });
          state[nearIdx] = { ...state[nearIdx], ...newMessage };
          return [...state];
        }

        // Heurística adicional para mídia: quando a URL muda (objURL -> URL servidor),
        // tentar casar por mediaType + body normalizado + janela de tempo curta e status 'sending'.
        const normalizeBody = (b) => {
          try {
            const s = (b || '').replace(/\u200d/g, '');
            return s.replace(/^\*[^*]+:\*\s*\n+\n?/, '').trim();
          } catch { return (b || ''); }
        };
        const newBodyNorm = normalizeBody(newMessage.body);
        const nearByMetaIdx = state.findIndex((m) => {
          if (!m?.fromMe || !m?.mediaType) return false;
          if (m.mediaType !== newMessage.mediaType) return false;
          const mTs = new Date(m.createdAt || m.timestamp || 0).getTime();
          if (Math.abs(newTs - mTs) > 7000) return false; // 7s de tolerância
          // Preferir casar apenas com temporárias/enviando
          const isTemp = typeof m.id === 'string' && m.id.startsWith('temp-');
          const isSending = (m.status === 'sending');
          if (!isTemp && !isSending) return false;
          return normalizeBody(m.body) === newBodyNorm;
        });
        if (nearByMetaIdx !== -1) {
          dbg('[dedupe] merge by mediaType+body/time', { id: newMessage.id, mediaType: newMessage.mediaType });
          state[nearByMetaIdx] = { ...state[nearByMetaIdx], ...newMessage };
          return [...state];
        }

        // Dedupe específico para áudio: ignorar body/caption e casar apenas por mediaType === 'audio'
        if (newMessage.mediaType === 'audio') {
          const nearAudioIdx = state.findIndex((m) => {
            if (!m?.fromMe) return false;
            if (m.mediaType !== 'audio') return false;
            const mTs = new Date(m.createdAt || m.timestamp || 0).getTime();
            // janela maior porque upload/conversão pode levar mais
            if (Math.abs(newTs - mTs) > 20000) return false;
            const isTemp = typeof m.id === 'string' && m.id.startsWith('temp-');
            const isSending = (m.status === 'sending');
            return isTemp || isSending;
          });
          if (nearAudioIdx !== -1) {
            dbg('[dedupe] merge audio by type/time (ignore body)', { id: newMessage.id });
            state[nearAudioIdx] = { ...state[nearAudioIdx], ...newMessage };
            return [...state];
          }
        }
      }
      // Deduplicação específica para CONTACT (vCard): casar por mediaType e janela de tempo, ignorando diferenças no body
      if (isFromMe && newMessage.mediaType === 'contactMessage') {
        const newTs = new Date(newMessage.createdAt || newMessage.timestamp || Date.now()).getTime();
        const nearContactIdx = state.findIndex((m) => {
          if (!m?.fromMe) return false;
          if (m.mediaType !== 'contactMessage') return false;
          const mTs = new Date(m.createdAt || m.timestamp || 0).getTime();
          if (Math.abs(newTs - mTs) > 7000) return false; // 7s de tolerância
          const isTemp = typeof m.id === 'string' && m.id.startsWith('temp-');
          const isSending = (m.status === 'sending');
          return isTemp || isSending;
        });
        if (nearContactIdx !== -1) {
          dbg('[dedupe] merge contact by type/time (ignore body)', { id: newMessage.id });
          state[nearContactIdx] = { ...state[nearContactIdx], ...newMessage };
          return [...state];
        }
      }

      // Deduplicação defensiva para TEXTO fromMe (sem mídia): normaliza corpo removendo assinatura e compara janela curta
      if (isFromMe && !hasMedia) {
        const normalizeBody = (b) => {
          try {
            const s = (b || '').replace(/\u200d/g, '');
            // remove assinatura do tipo *Nome:*\n\n
            return s.replace(/^\*[^*]+:\*\s*\n+\n?/, '').trim();
          } catch { return (b || ''); }
        };
        const newBodyNorm = normalizeBody(newMessage.body);
        const newTs = new Date(newMessage.createdAt || newMessage.timestamp || Date.now()).getTime();
        const nearIdx = state.findIndex((m) => {
          if (!m?.fromMe || m?.mediaUrl) return false;
          const mTs = new Date(m.createdAt || m.timestamp || 0).getTime();
          if (Math.abs(newTs - mTs) > 5000) return false; // janela 5s
          return normalizeBody(m.body) === newBodyNorm;
        });
        if (nearIdx !== -1) {
          dbg('[dedupe] merge by text body/time', { id: newMessage.id });
          state[nearIdx] = { ...state[nearIdx], ...newMessage };
          return [...state];
        }
      }
      dbg('[dedupe] push new', { id: newMessage.id, clientMessageId: newMessage.clientMessageId, mediaType: newMessage.mediaType });
      state.push(newMessage);
    }

    return [...state];
  }
  if (action.type === MESSAGE_ACTIONS.ADD_REACTION) {
    const messageReaction = action.payload;
    const messageIndex = state.findIndex(
      (m) => m.id === messageReaction.quotedMsgId
    );
    const reactionObject = {
      id: messageReaction.id,
      fromMe: messageReaction.fromMe,
      body: messageReaction.body,
      contactId: messageReaction.contactId,
      createdAt: messageReaction.createdAt,
      // Preservar informações do contato se disponíveis
      contact: messageReaction.contact || null,
      contactName: messageReaction.contactName || null,
    };

    if (messageIndex !== -1) {
      if (state[messageIndex].hasOwnProperty("reactionsMsg")) {
        state[messageIndex].reactionsMsg.push(reactionObject)
      } else {

        state[messageIndex].reactionsMsg = [reactionObject]
      }
    }
    return [...state];
  }

  if (action.type === MESSAGE_ACTIONS.DELETE_REACTION) {
    const messageReaction = action.payload;
    let messageIndex = state.findIndex(
      (m) => m.id === messageReaction.messageId
    );

    if (messageIndex !== -1) {

      let reactionIndex
      if (messageReaction.fromMe) {
        reactionIndex = state[messageIndex].reactionsMsg.findIndex((r) => r.id === messageReaction.reactionId && r.fromMe)
      } else {
        reactionIndex = state[messageIndex].reactionsMsg.findIndex((r) => r.id === messageReaction.reactionId && r.contactId == messageReaction.contactId)
      }
      if (reactionIndex !== -1) {

        state[messageIndex].reactionsMsg.splice(reactionIndex, 1);
      }
    }
    return [...state];
  }

  if (action.type === MESSAGE_ACTIONS.DELETE_MESSAGE) {
    const messageId = action.payload;
    return state.filter((message) => message.id !== messageId);
  }

  if (action.type === MESSAGE_ACTIONS.UPDATE_MESSAGE) {
    const mtu = action.payload;
    const messageToUpdate = mtu && typeof mtu === 'object'
      ? { ...mtu, isPrivate: mtu?.isPrivate === true || mtu?.isPrivate === 'true' }
      : mtu;
    let messageIndex = -1;
    if (messageToUpdate.clientMessageId) {
      messageIndex = state.findIndex(
        (m) => m.clientMessageId && m.clientMessageId === messageToUpdate.clientMessageId
      );
    }
    if (messageIndex === -1) {
      messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);
    }
    // Fallback: tentar casar por WID quando não encontra por clientMessageId/id
    if (messageIndex === -1 && messageToUpdate.wid) {
      messageIndex = state.findIndex((m) => m.wid && m.wid === messageToUpdate.wid);
    }

    if (messageIndex !== -1) {
      state[messageIndex] = { ...state[messageIndex], ...messageToUpdate };
    }

    return [...state];
  }

  if (action.type === MESSAGE_ACTIONS.RESET) {
    return [];
  }
};

const MessagesList = ({ isGroup, onDrop, whatsappId, queueId, channel }) => {
  const globalPlayer = useAudioPlayer();
  const theme = useTheme();
  const classes = useMessageListAllStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messagesList; }, [messagesList]);
  const [pageNumber, setPageNumber] = useState(PAGINATION_CONFIG.INITIAL_PAGE);
  // Controle de autoscroll: verdadeiro quando usuário está no fim (ou próximo)
  const isAtBottomRef = useRef(true);
  const prefetchDoneRef = useRef(false);
  const scrollRafId = useRef(null);
  
  const history = useHistory();
  const lastMessageRef = useRef();
  const messageRefs = useRef({});
  const messagesContainerRef = useRef(null);
  const [floatingDayLabel, setFloatingDayLabel] = useState("");
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const { ticketId } = useParams();
  const { user, socket } = useContext(AuthContext);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);
  
  // 🚀 Estados simples para carregamento (sem cache)
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  
  // 🎯 Estados para menu de opções das mensagens
  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();

  const [dragActive, setDragActive] = useState(false);
  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { showSelectMessageCheckbox, clearSelection } = useContext(ForwardMessageContext);
    // 🎯 Hook de Reações Modularizado (Fase 4)
  const reactionHandlers = useMessageReactions(isGroup, dispatch);
  const {
    reactionModalOpen,
    selectedMessageReactions,
    reactionUpdateTrigger,
    contarOcorrencias,
    handleFloatingReact,
    handleOpenReactionDetails,
    handleCloseReactionDetails,
    forceReactionUpdate,
    hasConsecutiveReactions,
  } = reactionHandlers;  // 🎯 Hook de Ações de Mensagem Modularizado (Fase 5)
  const messageActionHandlers = useMessageActions();
  const {
    hoveredMessageId,
    handleMessageHover,
    handleMessageLeave,
    handleMenuMouseEnter,
    handleMenuMouseLeave,
    handleFloatingReply,
    handleFloatingForward,
    handleDoubleClickMessage
  } = messageActionHandlers;

  // 🎬 Hook de Renderização de Mídia Modularizado (Fase 6)
  const mediaRenderer = useMediaRenderer(messagesList);
  const renderMedia = mediaRenderer.renderMedia;

  // 🎵 Direct Audio Renderer - Simplified approach for audio messages
  const renderAudioDirect = useCallback((message, messageIndex) => {
    if (message.mediaType === 'audio') {
      const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
      const uuidFromRoute = isUuid(ticketId) ? ticketId : null;
      const buildAudioQueue = (startMessageId) => {
        // Regra: reproduzir áudios consecutivos do MESMO contato (direção)
        // até encontrar uma mensagem não- áudio ou de outra direção.
        const list = messagesList || [];
        const startIdx = list.findIndex(m => m && m.id === startMessageId);
        if (startIdx < 0) {
          // Fallback: fila unitária
          const clicked = message;
          const tUuid = clicked?.ticket?.uuid || (isUuid(clicked?.ticketUuid) ? clicked.ticketUuid : uuidFromRoute);
          return {
            tracks: [{
              id: clicked.id,
              url: clicked.mediaUrl,
              title: clicked.filename || clicked.originalName || 'Áudio',
              ticketId: clicked.ticketId,
              ticketUuid: tUuid,
              ticketRouteId: uuidFromRoute,
              contactName: clicked.contact?.name,
              index: 0,
            }],
            startIndex: 0
          };
        }

        const dirFromMe = !!list[startIdx]?.fromMe;
        const tracks = [];
        let idxCounter = 0;
        for (let i = startIdx; i < list.length; i++) {
          const m = list[i];
          if (!m) break;
          // Ignora mensagens de reação no caminho
          if (m.mediaType === MESSAGE_MEDIA_TYPES.REACTION) {
            continue;
          }
          // Interrompe se mudar o tipo ou a direção
          if (m.mediaType !== 'audio') break;
          if (!!m.fromMe !== dirFromMe) break;
          const tUuid = m?.ticket?.uuid || (isUuid(m?.ticketUuid) ? m.ticketUuid : uuidFromRoute);
          tracks.push({
            id: m.id,
            url: m.mediaUrl,
            title: m.filename || m.originalName || 'Áudio',
            ticketId: m.ticketId,
            ticketUuid: tUuid,
            ticketRouteId: uuidFromRoute,
            contactName: m.contact?.name,
            index: idxCounter++,
          });
        }

        // Garantia mínima: se por algum motivo não coletou, volta fila unitária
        if (!tracks.length) {
          const clicked = list[startIdx];
          const tUuid = clicked?.ticket?.uuid || (isUuid(clicked?.ticketUuid) ? clicked.ticketUuid : uuidFromRoute);
          return {
            tracks: [{
              id: clicked.id,
              url: clicked.mediaUrl,
              title: clicked.filename || clicked.originalName || 'Áudio',
              ticketId: clicked.ticketId,
              ticketUuid: tUuid,
              ticketRouteId: uuidFromRoute,
              contactName: clicked.contact?.name,
              index: 0,
            }],
            startIndex: 0
          };
        }

        // Sempre iniciamos no primeiro item da sequência contígua
        return { tracks, startIndex: 0 };
      };
      return <WhatsAppAudioPlayer message={message} messageIndex={messageIndex} formatMessageTime={formatMessageTime} buildAudioQueue={buildAudioQueue} />;
    }
    return null;
  }, [messagesList, ticketId]);

  // 📇 Direct Contact Renderer - Simplified approach for contact messages
  const renderContactDirect = useCallback((message, whatsappId, queueId) => {
    if (message.mediaType === 'contactMessage') {
      // Preferir o corpo vCard do servidor se disponível
      if (message.body && typeof message.body === 'string' && message.body.trim() !== '') {
        const { contact, numbers } = parseContactMessage(message.body);
        return (
          <VcardPreview 
            contact={contact} 
            numbers={numbers} 
            whatsappId={whatsappId}
            queueId={queueId}
          />
        );
      }

      // Fallback para a mensagem temporária: usar os dados do vCard enviados pelo front
      if (message.vCard && (message.vCard.name || message.vCard.number)) {
        const contact = message.vCard.name || '';
        const numbers = message.vCard.number || '';
        return (
          <VcardPreview 
            contact={contact}
            numbers={numbers}
            whatsappId={whatsappId}
            queueId={queueId}
          />
        );
      }

      // Último recurso: placeholder amigável (evita impressão de erro)
      return <div style={{ color: '#9e9e9e', fontSize: '12px' }}>Preparando contato…</div>;
    }
    return null;
  }, []);

  // 🚀 Cálculo de IDs das filas memoizado
  const userQueueIds = useMemo(() => user.queues.map((q) => q.id), [user.queues]);
  const companyId = user.companyId;

  // (Removido) Escuta duplicada de eventos locais para mensagens temporárias

  useEffect(() => {
    async function fetchData() {
      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {
        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value;
        if (key === "enableLGPD") settingEnableLGPD = value;
      }
      if (
        settingEnableLGPD === "enabled" &&
        settinglgpdDeleteMessage === "enabled"
      ) {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    dispatch({ type: MESSAGE_ACTIONS.RESET });
    setPageNumber(PAGINATION_CONFIG.INITIAL_PAGE);    currentTicketId.current = ticketId;  }, [ticketId, selectedQueuesMessage]);

  // 🔄 Ao trocar de conversa, limpar seleção de encaminhamento (evita levar seleção para outra conversa)
  useEffect(() => {
    try { clearSelection && clearSelection(); } catch {}
    // Também limpa na desmontagem deste componente
    return () => { try { clearSelection && clearSelection(); } catch {} };
  }, [ticketId, clearSelection]);

  // Escuta eventos locais para inserir/atualizar mensagens temporárias imediatamente
  useEffect(() => {
    const handleAddTemp = (e) => {
      const { ticketId: evTicketId, message } = e.detail || {};
      if (!evTicketId || String(evTicketId) !== String(currentTicketId.current)) return;
      if (!message) return;
      dispatch({ type: MESSAGE_ACTIONS.ADD_MESSAGE, payload: message });
      // Auto-scroll apenas se o usuário estiver próximo ao fim
      try { if (isAtBottomRef.current) scrollToBottom(lastMessageRef); } catch {}
    };

    const handleUpdateTemp = (e) => {
      const { ticketId: evTicketId, clientMessageId, data } = e.detail || {};
      if (!evTicketId || String(evTicketId) !== String(currentTicketId.current)) return;
      if (!clientMessageId || !data) return;
      dispatch({ type: MESSAGE_ACTIONS.UPDATE_MESSAGE, payload: { ...data, clientMessageId } });
    };

    window.addEventListener('chat:add-temp-message', handleAddTemp);
    window.addEventListener('chat:update-temp-message', handleUpdateTemp);
    return () => {
      window.removeEventListener('chat:add-temp-message', handleAddTemp);
      window.removeEventListener('chat:update-temp-message', handleUpdateTemp);
    };
  }, []);
  // 🎯 Pré-calcular estilos de espaçamento para evitar setState durante render
  const messageSpacingStyles = useMemo(() => {
    const styles = {};
    
    messagesList.forEach((message, index) => {
      const currentMessage = messagesList[index];
      const previousMessage = messagesList[index - 1];
      
      // Verificar se a mensagem atual tem reações (estado em tempo real)
      const hasReactions = currentMessage?.reactionsMsg?.length > 0;
      
      // Verificar se a mensagem anterior tem reações (estado em tempo real)
      const previousHasReactions = previousMessage?.reactionsMsg?.length > 0;
      
      // 👥 NOVA LÓGICA: Espaçamento extra quando muda o usuário (16px como no original)
      let userChangeSpacing = "0px";
      if (index > 0 && previousMessage) {
        const currentFromMe = currentMessage.fromMe;
        const previousFromMe = previousMessage.fromMe;
        if (currentFromMe !== previousFromMe) {
          userChangeSpacing = "16px"; // Espaço extra quando muda de usuário
        }
      }
      
      // 🎯 LÓGICA COMBINADA: Reações + Mudança de usuário
      if (previousHasReactions) {
        styles[message.id] = {
          marginTop: userChangeSpacing === "16px" ? "20px" : "12px", // Combina espaços se necessário
          marginBottom: hasReactions ? "8px" : "2px"
        };
      }
      // Se apenas a mensagem atual tem reações
      else if (hasReactions) {
        styles[message.id] = {
          marginTop: userChangeSpacing, // Usa espaçamento de mudança de usuário
          marginBottom: "8px" // Espaço para as reações abaixo
        };
      }
      // 🎯 MENSAGENS NORMAIS - espaçamento padrão + mudança de usuário
      else {
        styles[message.id] = {
          marginTop: userChangeSpacing,
          marginBottom: "2px"
        };
      }
    });
    
    return styles;
  }, [messagesList, reactionUpdateTrigger]); // 🔧 IMPORTANTE: Dependências para reatividade

  // 🎯 Função para obter o espaçamento dinâmico (agora apenas consulta o cache)
  const getMessageSpacing = useCallback((message, index) => {
    return messageSpacingStyles[message.id] || {
      marginTop: "0px",
      marginBottom: "2px"
    };
  }, [messageSpacingStyles]);
  
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(async () => {
      if (ticketId === "undefined") {
        history.push("/tickets");
        return;
      }
      if (isNil(ticketId)) return;
      
      try {
        // 🌐 Chamada direta à API (sem cache)
        const userQueueIds = user?.queues?.map(q => q.id) || [];
        const queueMessage = selectedQueuesMessage?.length > 0 
          ? selectedQueuesMessage 
          : userQueueIds;
        
        const { data } = await api.get("/messages/" + ticketId, {
          params: {
            pageNumber,
            selectedQueues: JSON.stringify(queueMessage),
          },
        });
        
        if (currentTicketId.current === ticketId && data) {
          dispatch({ type: MESSAGE_ACTIONS.LOAD_MESSAGES, payload: data.messages });
          setHasMore(data.hasMore);
          setLoading(false);
          setLoadingMore(false);
          if (!prefetchDoneRef.current && data.hasMore && pageNumber === PAGINATION_CONFIG.INITIAL_PAGE) {
            prefetchDoneRef.current = true;
            setPageNumber((prev) => prev + 1);
          }
          
          if (pageNumber === PAGINATION_CONFIG.INITIAL_PAGE && data.messages?.length > 1) {
            scrollToBottomRef();
          }
        }
      } catch (err) {
        setLoading(false);
        toastError(err);
        setLoadingMore(false);
      }
    }, TIMING_CONFIG.DEBOUNCE_DELAY);
      
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, selectedQueuesMessage, user, history]);

  // Ao trocar de conversa (ticketId muda), se houver áudio local tocando, faz handoff para o player global
  useEffect(() => {
    return () => {
      try {
        const current = audioSequenceManager.getCurrentPlaying();
        if (!current) return;
        const playingEl = current.audioRef?.current;
        if (!playingEl) return;
        const isPlaying = !playingEl.paused && !playingEl.ended && playingEl.currentTime > 0;
        if (!isPlaying) return;
        if (!globalPlayer) return;

        const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
        const uuidFromRoute = isUuid(ticketId) ? ticketId : null;
        const audios = messagesList
          .filter(m => m.mediaType === 'audio')
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          .map((m, idx) => ({
            id: m.id,
            url: m.mediaUrl,
            title: m.filename || m.originalName || 'Áudio',
            ticketId: m.ticketId,
            ticketUuid: m.ticket?.uuid || (isUuid(m.ticketUuid) ? m.ticketUuid : uuidFromRoute),
            ticketRouteId: uuidFromRoute,
            contactName: m.contact?.name,
            index: idx,
          }));
        const startIndex = audios.findIndex(a => a.id === current.messageId);
        globalPlayer.handoffFromElement(playingEl, audios, startIndex >= 0 ? startIndex : 0);
      } catch (e) {
        // noop
      }
    };
  }, [ticketId, messagesList, globalPlayer]);

  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }

    const companyId = user.companyId;

    //    const socket = socketManager.GetSocket();
    const connectEventMessagesList = () => {
      socket.emit("joinChatBox", `${ticketId}`);
    };

    const onAppMessageMessagesList = (data) => {
      // Debug opcional controlado por flag
      const debug = () => (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('debugChatReconcile') === '1');
      if (debug()) console.groupCollapsed('[socket:create]', { ticket: ticketId, action: data?.action, mid: data?.message?.id, cmid: data?.message?.clientMessageId, mediaType: data?.message?.mediaType });

      const eventTicketUuid = data?.ticket?.uuid || data?.message?.ticket?.uuid;
      if (
        data.action === "create" &&
        eventTicketUuid === ticketId &&
        data?.message?.mediaType !== MESSAGE_MEDIA_TYPES.REACTION
      ) {
        const serverMsg = data.message;
        const isFromMe = !!serverMsg?.fromMe;

        // Heurística de reconciliação para evitar duplicação de mensagens de saída sem clientMessageId
  if (isFromMe) {
          // Se o servidor já trouxe clientMessageId, preferimos UPDATE para casar com a temporária
          if (serverMsg.clientMessageId) {
            if (debug()) console.log('> update by clientMessageId');
            dispatch({ type: MESSAGE_ACTIONS.UPDATE_MESSAGE, payload: serverMsg });
            if (isAtBottomRef.current) scrollToBottom(lastMessageRef);
            if (debug()) console.groupEnd();
            return;
          }

          const normalizeBody = (b) => {
            try {
              const s = (b || '').replace(/\u200d/g, '');
              return s.replace(/^\*[^*]+:\*\s*\n+\n?/, '').trim();
            } catch { return (b || ''); }
          };
          const sTs = new Date(serverMsg.createdAt || serverMsg.timestamp || Date.now()).getTime();
          const sBodyNorm = normalizeBody(serverMsg.body);
          let matched = null;
          // Procurar de trás para frente para pegar a temporária mais recente
          const arr = messagesRef.current || [];
          for (let i = arr.length - 1; i >= 0; i--) {
            const m = arr[i];
            if (!m?.fromMe) continue;
            // Evita casar com mensagens já consolidadas com id numérico
            const isTemp = typeof m.id === 'string' && m.id.startsWith('temp-');
            const isSending = (m.status === 'sending');
            if (!isTemp && !isSending) continue;

            // MediaType deve bater se houver
            if (serverMsg.mediaType || m.mediaType) {
              if (serverMsg.mediaType !== m.mediaType) continue;
            }

            // Janela de tempo
            const mTs = new Date(m.createdAt || m.timestamp || 0).getTime();
            // Áudio em produção pode demorar mais: ampliar janela
            const windowMs = (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('reconcileWindowMs'))
              ? parseInt(window.localStorage.getItem('reconcileWindowMs'), 10)
              : (serverMsg.mediaType === 'audio' ? 20000 : 7000);
            if (Math.abs(sTs - mTs) > windowMs) continue;

            // Texto/caption normalizado: para áudio e contato, ignorar diferença de body (vCard/filename vs vazio)
            if (serverMsg.mediaType !== 'audio' && serverMsg.mediaType !== 'contactMessage') {
              if (normalizeBody(m.body) !== sBodyNorm) continue;
            }

            matched = m;
            break;
          }

          if (matched) {
            // Injeta clientMessageId da temporária para atualizar corretamente (mergeará id do servidor)
            const payload = { ...serverMsg, clientMessageId: matched.clientMessageId };
            if (debug()) console.log('> update by heuristic', { matchedId: matched.id, matchedClientMessageId: matched.clientMessageId });
            dispatch({ type: MESSAGE_ACTIONS.UPDATE_MESSAGE, payload });
            if (isAtBottomRef.current) scrollToBottom(lastMessageRef);
            if (debug()) console.groupEnd();
            return;
          }
        }

        // Fallback: adicionar normalmente
        if (debug()) console.log('> add fallback');
        dispatch({ type: MESSAGE_ACTIONS.ADD_MESSAGE, payload: data.message });
        if (isAtBottomRef.current) scrollToBottom(lastMessageRef);
        if (debug()) console.groupEnd();
        return;
      }

      if (
        data.action === "update" &&
        data?.message?.ticket?.uuid === ticketId &&
        data?.message?.mediaType !== MESSAGE_MEDIA_TYPES.REACTION
      ) {
        dispatch({ type: MESSAGE_ACTIONS.UPDATE_MESSAGE, payload: data.message });
      }

      // Atualização específica para reações via socket
      if (
        data.action === "update" &&
        data?.ticket?.uuid === ticketId &&
        data?.message
      ) {
        dispatch({ type: MESSAGE_ACTIONS.UPDATE_MESSAGE, payload: data.message });
      }      if (
        data.action === "create" &&
        data?.message?.ticket.uuid === ticketId &&
        data?.message?.mediaType === MESSAGE_MEDIA_TYPES.REACTION      ) {
        dispatch({ type: MESSAGE_ACTIONS.ADD_REACTION, payload: data.message });
        // 🔄 NOVO: Força atualização do layout quando reação é adicionada
        forceReactionUpdate();
      }

      if (
        data.action == "delete" &&
        data?.message?.ticket?.uuid === ticketId &&
        data?.message?.mediaType !== MESSAGE_MEDIA_TYPES.REACTION
      ) {
        dispatch({ type: MESSAGE_ACTIONS.DELETE_MESSAGE, payload: data.messageId });
      }      if (
        data.action == "delete" &&
        data?.message?.uuid === ticketId &&
        data?.message?.mediaType === MESSAGE_MEDIA_TYPES.REACTION
      ) {        dispatch({ type: MESSAGE_ACTIONS.DELETE_REACTION, payload: data.message });
        // 🔄 NOVO: Força atualização do layout quando reação é removida
        forceReactionUpdate();
      }
    };
    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    return () => {
      socket.emit("joinChatBoxLeave", `${ticketId}`);

      socket.off("connect", connectEventMessagesList);
      socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
    };
  }, [ticketId]);
  // 🚀 Handlers memoizados para melhor performance
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  }, [loadingMore]);

  const scrollToBottomRef = useCallback(() => {
    scrollToBottom(lastMessageRef);
  }, []);

  // 🎯 Handlers para menu de opções das mensagens
  const handleOpenMessageOptionsMenu = useCallback((e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  }, []);

  const handleCloseMessageOptionsMenu = useCallback((e) => {
    setAnchorEl(null);
    setSelectedMessage({});
  }, []);

  const formatDayLabel = useCallback((dateValue) => {
    if (!dateValue) return "";
    const parsed = typeof dateValue === "string" ? parseISO(dateValue) : new Date(dateValue);
    const today = new Date();
    if (isSameDay(parsed, today)) return "Hoje";
    if (isSameDay(parsed, subDays(today, 1))) return "Ontem";
    return format(parsed, "dd/MM/yyyy");
  }, []);

  const updateFloatingDayLabel = useCallback((scrollTopValue) => {
    if (!messagesList.length) {
      setFloatingDayLabel("");
      return;
    }

    const scrollTop = typeof scrollTopValue === "number"
      ? scrollTopValue
      : (messagesContainerRef.current?.scrollTop || 0);

    const refs = messageRefs.current;
    for (let i = 0; i < messagesList.length; i += 1) {
      const msg = messagesList[i];
      const el = refs[msg?.id];
      if (!el) continue;
      const offsetTop = el.offsetTop;
      const height = el.offsetHeight || 0;
      if (offsetTop + height >= scrollTop + 1) {
        setFloatingDayLabel(formatDayLabel(msg?.createdAt));
        return;
      }
    }

    setFloatingDayLabel(formatDayLabel(messagesList[messagesList.length - 1]?.createdAt));
  }, [messagesList, formatDayLabel]);

  const handleScroll = useCallback((e) => {
    const el = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollRafId.current) cancelAnimationFrame(scrollRafId.current);
    scrollRafId.current = requestAnimationFrame(() => {
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;
      isAtBottomRef.current = distanceToBottom <= 80;
      handleScrollLoadMore(scrollTop, hasMore, loading, loadMore);
      updateFloatingDayLabel(scrollTop);
    });
  }, [hasMore, loading, loadMore, updateFloatingDayLabel]);

  // �️ Drag & Drop handlers modularizados
  const { handleDrag, handleDrop } = useDragAndDrop(onDrop, setDragActive);// 🚀 Regex patterns memoizadas
  const xmlRegex = useMemo(() => /<([^>]+)>/g, []);

  const registerMessageRef = useCallback((id) => (el) => {
    if (!id) return;
    if (el) {
      messageRefs.current[id] = el;
    } else {
      delete messageRefs.current[id];
    }
  }, []);

  const messageMeta = useMemo(() => {
    return messagesList.map((msg, index) => {
      const currentDate = msg?.createdAt ? parseISO(msg.createdAt) : null;
      const previousDate = index > 0 && messagesList[index - 1]?.createdAt
        ? parseISO(messagesList[index - 1].createdAt)
        : null;
      const isFirstOfDay = !!currentDate && (index === 0 || !previousDate || !isSameDay(currentDate, previousDate));
      const dayLabel = isFirstOfDay && currentDate ? formatDayLabel(currentDate) : null;

      let ticketSeparatorProps = null;
      if (index < messagesList.length - 1) {
        const next = messagesList[index + 1];
        if (next && msg.ticketId !== next.ticketId) {
          ticketSeparatorProps = {
            ticketId: next.ticketId,
            status: next.status,
            createdAt: next.createdAt,
            key: `ticket-separator-${msg.id}`,
          };
        }
      }

      return { msg, index, isFirstOfDay, dayLabel, ticketSeparatorProps };
    });
  }, [messagesList, formatDayLabel]);

  // 🚀 Renderizador de divisores de mensagens memoizado (corrige erro em CallLogMessage)
  const renderMessageDivider = useCallback((message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;
      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
    return null;
  }, [messagesList]);

  const renderMessages = useCallback(() => {
    if (messagesList.length > 0) {
      // Atualiza a lista de mensagens no gerenciador de sequência de áudio
      audioSequenceManager.updateMessagesList(messagesList);
      
      const viewMessagesList = messageMeta.map(({ msg: message, index, isFirstOfDay, dayLabel, ticketSeparatorProps }) => {

        // ✅ Validação robusta para evitar erros de renderização
        if (!message || !message.id) {
          console.warn('⚠️ Mensagem inválida encontrada:', message);
          return null;
        }

        const reactionList = contarOcorrencias(message.reactionsMsg || []);
        const isLastMessage = index === messagesList.length - 1;
        const daySeparator = isFirstOfDay && dayLabel ? (
          <div className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
            <div className={classes.dailyTimestampText}>{dayLabel}</div>
          </div>
        ) : null;

        const ticketSeparator = ticketSeparatorProps ? (
          <TicketSeparator
            key={ticketSeparatorProps.key}
            ticketId={ticketSeparatorProps.ticketId}
            status={ticketSeparatorProps.status}
            createdAt={ticketSeparatorProps.createdAt}
            themeColors={theme.palette}
          />
        ) : null;
        const wrapperRef = registerMessageRef(message.id);

        // ✅ Renderização de chamadas perdidas com validação extra
        if (message.mediaType === "call_log") {
          return (
            <div
              key={message.id}
              ref={wrapperRef}
              data-message-id={message.id}
              style={{ display: "flex", flexDirection: "column", alignItems: message.fromMe ? "flex-end" : "flex-start", width: "100%" }}
            >
              {daySeparator}
              {ticketSeparator}
              <MessageItemErrorBoundary message={message}>
                <CallLogMessage
                  message={message}
                  index={index}
                  isGroup={isGroup}
                  classes={classes}
                  renderMessageDivider={renderMessageDivider}
                  formatMessageTime={formatMessageTime}
                  i18n={i18n}
                />
              </MessageItemErrorBoundary>
              {isLastMessage && (
                <div
                  ref={lastMessageRef}
                  style={{ float: "left", clear: "both" }}
                />
              )}
            </div>
          );
        }

        if (!message.fromMe) {
          return (
            <div
              key={message.id}
              ref={wrapperRef}
              data-message-id={message.id}
              style={{ display: "flex", flexDirection: "column", alignItems: message.fromMe ? "flex-end" : "flex-start", width: "100%" }}
            >
              {daySeparator}
              {ticketSeparator}
              <MessageItemErrorBoundary message={message}>
                <MessageBubbleLeft
                  key={`${message.id}-${message.reactionsMsg?.length || 0}-${message.reactionsMsg?.map(r => r.id).join('-') || 'no-reactions'}`}
                  message={message}
                  index={index}
                  classes={classes}
                  isGroup={isGroup}
                  hoveredMessageId={hoveredMessageId}
                  whatsappId={whatsappId}
                  queueId={queueId}
                  showSelectMessageCheckbox={showSelectMessageCheckbox}
                  lgpdDeleteMessage={lgpdDeleteMessage}
                  xmlRegex={xmlRegex}
                  reactionList={reactionList}
                  getMessageSpacing={getMessageSpacing}
                  // Event handlers
                  handleDoubleClickMessage={handleDoubleClickMessage}
                  handleMessageHover={handleMessageHover}
                  handleMessageLeave={handleMessageLeave}
                  handleFloatingReply={handleFloatingReply}
                  handleFloatingForward={handleFloatingForward}
                  handleFloatingReact={handleFloatingReact}
                  handleMenuMouseEnter={handleMenuMouseEnter}
                  handleMenuMouseLeave={handleMenuMouseLeave}
                  handleOpenReactionDetails={handleOpenReactionDetails}
                  handleOpenMessageOptionsMenu={handleOpenMessageOptionsMenu}
                  // Funções de renderização
                  renderMedia={renderMedia}
                  renderAudioDirect={renderAudioDirect}
                  renderContactDirect={renderContactDirect}
                />
              </MessageItemErrorBoundary>
              {isLastMessage && (
                <div
                  ref={lastMessageRef}
                  style={{ float: "left", clear: "both" }}
                />
              )}
            </div>
          );
        }

          return (
            <div
              key={message.id}
              ref={wrapperRef}
              data-message-id={message.id}
              style={{ display: "flex", flexDirection: "column", alignItems: message.fromMe ? "flex-end" : "flex-start", width: "100%" }}
            >
            {daySeparator}
            {ticketSeparator}
            <MessageItemErrorBoundary message={message}>
              <MessageBubbleRight
                key={`${message.id}-${message.reactionsMsg?.length || 0}-${message.reactionsMsg?.map(r => r.id).join('-') || 'no-reactions'}`}
                message={message}
                index={index}
                classes={classes}
                isGroup={isGroup}
                hoveredMessageId={hoveredMessageId}
                whatsappId={whatsappId}
                queueId={queueId}
                showSelectMessageCheckbox={showSelectMessageCheckbox}
                lgpdDeleteMessage={lgpdDeleteMessage}
                xmlRegex={xmlRegex}
                reactionList={reactionList}
                getMessageSpacing={getMessageSpacing}
                // Event handlers
                handleDoubleClickMessage={handleDoubleClickMessage}
                handleMessageHover={handleMessageHover}
                handleMessageLeave={handleMessageLeave}
                handleFloatingReply={handleFloatingReply}
                handleFloatingForward={handleFloatingForward}
                handleFloatingReact={handleFloatingReact}
                handleMenuMouseEnter={handleMenuMouseEnter}
                handleMenuMouseLeave={handleMenuMouseLeave}
                handleOpenReactionDetails={handleOpenReactionDetails}
                handleOpenMessageOptionsMenu={handleOpenMessageOptionsMenu}
                // Funções de renderização
                renderMedia={renderMedia}
                renderAudioDirect={renderAudioDirect}
                renderContactDirect={renderContactDirect}
              />
            </MessageItemErrorBoundary>
            {isLastMessage && (
              <div
                ref={lastMessageRef}
                style={{ float: "left", clear: "both" }}
              />
            )}
          </div>
        );
      });
      // ✅ Filtrar elementos null antes de retornar
      return viewMessagesList.filter(Boolean);
    } else {
      return <div>Diga olá para seu novo contato!</div>;
    }  }, [
    messagesList,
    messageMeta,
    contarOcorrencias,
    registerMessageRef,
    isGroup,
    classes,
    formatMessageTime,
    xmlRegex,
    lastMessageRef,
    hoveredMessageId,
    handleMessageHover,
    handleMessageLeave,
    handleFloatingReply,
    handleFloatingForward,
    handleFloatingReact,
    handleOpenReactionDetails,
    handleOpenMessageOptionsMenu,
    whatsappId,
    queueId,
    renderMedia,
    renderAudioDirect,
    renderContactDirect,
    handleDoubleClickMessage,
    showSelectMessageCheckbox,
    lgpdDeleteMessage,
    getMessageSpacing,
    handleMenuMouseEnter,
    handleMenuMouseLeave,
    formatDayLabel,
    theme.palette
  ]);

  useEffect(() => {
    updateFloatingDayLabel(messagesContainerRef.current?.scrollTop || 0);
  }, [messagesList, updateFloatingDayLabel]);

  return (
    <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
      <DragDropOverlay
        dragActive={dragActive}
        classes={classes}
        handleDrag={handleDrag}
        handleDrop={handleDrop}
        message={SYSTEM_MESSAGES.DROP_FILE_HERE}
      />

      <div
        id="messagesList"
        className={classes.messagesList}
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {floatingDayLabel && (
          <div className={clsx(classes.dailyTimestamp, classes.floatingDayLabel)}>
            <span className={classes.dailyTimestampText}>{floatingDayLabel}</span>
          </div>
        )}

        {loadingMore && (
          <SkeletonLoader
            type="loadMore"
            count={3}
            showFeedback={true}
            loadingText={SYSTEM_MESSAGES.LOADING_MORE_MESSAGES}
          />
        )}

        {loading && messagesList.length === 0 ? (
          <SkeletonLoader
            type="initial"
            count={8}
            showMedia={true}
            showTyping={false}
            showFeedback={true}
            loadingText={SYSTEM_MESSAGES.LOADING_CONVERSATION}
          />
        ) : messagesList.length > 0 ? (
          renderMessages()
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              padding: "40px 16px",
              textAlign: "center",
              color: theme.palette.text.secondary,
            }}
          >
            Nenhum conversa encontrada, envie sua primeira mensagem para esse contato.
          </div>
        )}
      </div>

      {channel !== "whatsapp" && channel !== undefined && (
        <div
          style={{
            width: "100%",
            display: "flex",
            padding: "10px",
            alignItems: "center",
            backgroundColor: "#E1F3FB",
          }}
        >
          {channel === "facebook" ? <Facebook /> : <Instagram />}
          <span>{SYSTEM_MESSAGES.FACEBOOK_POLICY}</span>
        </div>
      )}

      {/* Modal para mostrar detalhes das reações */}
      <ReactionDetailsModal
        open={reactionModalOpen}
        onClose={handleCloseReactionDetails}
        reactions={selectedMessageReactions}
        isGroup={isGroup}
        messagesList={messagesList}
      />

      {/* Menu de opções das mensagens */}
      <MessageOptionsMenu
        anchorEl={anchorEl}
        message={selectedMessage}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        whatsappId={whatsappId}
        queueId={queueId}
      />
    </div>
  );
};

export default MessagesList;
